/**
 * 공용 이모지→SVG 아이콘 레지스트리.
 *
 * 규칙: 이모지를 UI 심볼로 쓰던 곳을 전부 이 아이콘들로 치환한다(이모지 금지).
 *  - 동일 이모지는 항상 동일 컴포넌트로 매핑한다(아래 EMOJI_ICON 맵 참조).
 *  - line 스타일(viewBox 0 0 24 24, strokeWidth 1.8) 기준. 채워야 자연스러운 것만 fill 사용.
 *  - size(기본 16) / color(기본 currentColor) props.
 *
 * 국기 이모지는 제외(별도 처리).
 */
import React from 'react';

export interface IconProps {
    size?: number;
    color?: string;
    className?: string;
    style?: React.CSSProperties;
    strokeWidth?: number;
}

const S = (size = 16): React.SVGProps<SVGSVGElement> => ({
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
});
const base = (p: IconProps) => ({ className: p.className, style: p.style });
const stroke = (p: IconProps) => ({
    stroke: p.color || 'currentColor',
    strokeWidth: p.strokeWidth ?? 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
});

// 📍 위치 핀
export const IcPin: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" {...stroke(p)} />
        <circle cx="12" cy="10" r="3" {...stroke(p)} />
    </svg>
);

// 📌 압정/고정
export const IcPushpin: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M9 3h6l-1 6 3 3v2H7v-2l3-3-1-6z" {...stroke(p)} />
        <path d="M12 14v7" {...stroke(p)} />
    </svg>
);

// 📅 📆 🗓️ 달력
export const IcCalendar: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <rect x="3" y="4.5" width="18" height="16" rx="2.5" {...stroke(p)} />
        <path d="M3 9h18M8 3v3M16 3v3" {...stroke(p)} />
    </svg>
);

// ✕ ❌ 닫기/취소
export const IcClose: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M6 6l12 12M18 6L6 18" {...stroke(p)} />
    </svg>
);

// ✓ 체크
export const IcCheck: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M5 12.5l4.5 4.5L19 6.5" {...stroke(p)} />
    </svg>
);

// ✅ ☑ 체크(원형)
export const IcCheckCircle: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <circle cx="12" cy="12" r="9" {...stroke(p)} />
        <path d="M8 12.5l2.5 2.5L16 9.5" {...stroke(p)} />
    </svg>
);

// ☐ 빈 체크박스
export const IcSquare: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <rect x="4" y="4" width="16" height="16" rx="3" {...stroke(p)} />
    </svg>
);

// 🏁 결승 깃발
export const IcFinishFlag: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M5 21V4" {...stroke(p)} />
        <path d="M5 5h13l-2.5 4L18 13H5" {...stroke(p)} />
    </svg>
);

// 🚩 깃발
export const IcFlag: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M5 21V4" {...stroke(p)} />
        <path d="M5 5h13l-2 4 2 4H5" {...stroke(p)} />
    </svg>
);

// 🏨 🛏️ 숙소(호텔)
export const IcHotel: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M3 19v-7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v7" {...stroke(p)} />
        <path d="M3 16h18M7 10V7a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v3M3 19v1M21 19v1" {...stroke(p)} />
    </svg>
);

// ⏰ 🕐 🕰 알람/시계
export const IcClock: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <circle cx="12" cy="12.5" r="8" {...stroke(p)} />
        <path d="M12 8.5v4.5l3 2M5 3L2.5 5.5M19 3l2.5 2.5" {...stroke(p)} />
    </svg>
);

// ⏳ 모래시계
export const IcHourglass: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M6 3h12M6 21h12M7 3c0 5 10 6 10 9s-10 4-10 9M17 3c0 5-10 6-10 9" {...stroke(p)} />
    </svg>
);

// ✈️ 🛫 비행기
export const IcPlane: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M21 15.5L3 11V8l3 1 4-3-1-3 2-1 3 5 5 1.5a1.6 1.6 0 0 1 0 3L13 13l-2 6h-2l1-4-3 .5-1 2H4l1.5-3.5z" {...stroke(p)} />
    </svg>
);

// 🏠 🏡 집
export const IcHome: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M4 11l8-6 8 6" {...stroke(p)} />
        <path d="M6 10v9h12v-9" {...stroke(p)} />
        <path d="M10 19v-5h4v5" {...stroke(p)} />
    </svg>
);

// 🍽️ 🍴 음식점(포크/나이프)
export const IcRestaurant: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M7 3v8M5 3v4a2 2 0 0 0 4 0V3M7 11v10M17 3c-1.5 0-2.5 2-2.5 5s1 4 2.5 4 2.5-1 2.5-4-1-5-2.5-5zM17 16v5" {...stroke(p)} />
    </svg>
);

// ☕ 🧋 카페(컵)
export const IcCafe: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M4 8h13v5a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5V8z" {...stroke(p)} />
        <path d="M17 9h2.5a2.5 2.5 0 0 1 0 5H17M7 3v2M11 3v2" {...stroke(p)} />
    </svg>
);

// 🍺 🍻 맥주
export const IcBeer: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M5 8h10v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V8z" {...stroke(p)} />
        <path d="M15 10h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-3M5 8c0-2 2-3 5-3s5 1 5 3" {...stroke(p)} />
    </svg>
);

// 🍾 🥂 🍷 샴페인/축배
export const IcChampagne: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M8 3h8M9 3c0 5 0 7 3 8 3-1 3-3 3-8M12 11v7M9 21h6" {...stroke(p)} />
    </svg>
);

// 👥 👫 사람들
export const IcUsers: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M16 19v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 17.5V19" {...stroke(p)} />
        <circle cx="10" cy="8" r="3.2" {...stroke(p)} />
        <path d="M20 19v-1.5a3.5 3.5 0 0 0-2.6-3.4M15.5 5.2a3.2 3.2 0 0 1 0 5.6" {...stroke(p)} />
    </svg>
);

// 👤 사람
export const IcUser: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <circle cx="12" cy="8" r="3.5" {...stroke(p)} />
        <path d="M5.5 20v-1a5 5 0 0 1 5-5h3a5 5 0 0 1 5 5v1" {...stroke(p)} />
    </svg>
);

// 🙋 🙆 🙅 ✋ 손 든 사람/손
export const IcRaiseHand: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M7 11V6a1.5 1.5 0 0 1 3 0v4M10 10V4.5a1.5 1.5 0 0 1 3 0V10M13 10V6a1.5 1.5 0 0 1 3 0v6c0 4-2 8-6 8s-6-3-6-6l1-2.5" {...stroke(p)} />
    </svg>
);

// 👋 인사
export const IcWave: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M8 12V5.5a1.5 1.5 0 0 1 3 0V11M11 11V4a1.5 1.5 0 0 1 3 0v7M14 11V6.5a1.5 1.5 0 0 1 3 0V13c0 4-2 7-6 7-3 0-5-2-6-4l-1.5-3a1.5 1.5 0 0 1 2.6-1.5L8 13" {...stroke(p)} />
    </svg>
);

// 👑 왕관
export const IcCrown: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M4 8l3 9h10l3-9-5 4-3-6-3 6-5-4z" {...stroke(p)} />
    </svg>
);

// 🏆 트로피
export const IcTrophy: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M7 4h10v5a5 5 0 0 1-10 0V4z" {...stroke(p)} />
        <path d="M7 6H4v1a3 3 0 0 0 3 3M17 6h3v1a3 3 0 0 1-3 3M12 14v3M9 21h6M9.5 21a2.5 2.5 0 0 1 5 0" {...stroke(p)} />
    </svg>
);

// 🥇 🥈 🥉 메달
export const IcMedal: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M8 3l2 5M16 3l-2 5" {...stroke(p)} />
        <circle cx="12" cy="15" r="6" {...stroke(p)} />
        <path d="M12 12.5l1 2 2 .2-1.5 1.4.4 2-1.9-1-1.9 1 .4-2L9.5 14.7l2-.2 1-2z" {...stroke(p)} />
    </svg>
);

// 🎉 🎊 축하(폭죽)
export const IcParty: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M3 21l5-13 9 9-14 4z" {...stroke(p)} />
        <path d="M14 4l1 1M19 3l-1 2M20 9l-2 .5M15 8l.5-2" {...stroke(p)} />
    </svg>
);

// ★ ⭐ 🌟 별(채움)
export const IcStar: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17.8 6.6 20l1-6.1L3.2 9.5l6.1-.9L12 3z" fill={p.color || 'currentColor'} />
    </svg>
);

// ☆ ♡ 🤍 ❤ 빈 별은 IcStarOutline
export const IcStarOutline: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17.8 6.6 20l1-6.1L3.2 9.5l6.1-.9L12 3z" {...stroke(p)} />
    </svg>
);

// ❤ 🤍 하트
export const IcHeart: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M12 20s-7-4.3-9-9a4.5 4.5 0 0 1 9-2 4.5 4.5 0 0 1 9 2c-2 4.7-9 9-9 9z" {...stroke(p)} />
    </svg>
);

// 💰 돈주머니
export const IcMoney: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M9 6h6l-1.5-2.5h-3L9 6z" {...stroke(p)} />
        <path d="M7 7c-2 2-3 5-3 8a5 5 0 0 0 5 5h6a5 5 0 0 0 5-5c0-3-1-6-3-8" {...stroke(p)} />
        <path d="M12 11v6M10.5 12.5h2.5a1.3 1.3 0 0 1 0 2.6h-2a1.3 1.3 0 0 0 0 2.6h2.5" {...stroke(p)} />
    </svg>
);

// 💸 지출(나가는 돈)
export const IcExpense: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <rect x="3" y="7" width="14" height="9" rx="2" {...stroke(p)} />
        <circle cx="10" cy="11.5" r="2" {...stroke(p)} />
        <path d="M20 9v9M22 11l-2-2-2 2" {...stroke(p)} />
    </svg>
);

// 💳 카드
export const IcCard: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <rect x="3" y="5" width="18" height="14" rx="2.5" {...stroke(p)} />
        <path d="M3 9.5h18M6.5 15h4" {...stroke(p)} />
    </svg>
);

// 💡 아이디어(전구)
export const IcBulb: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M9 18h6M10 21h4" {...stroke(p)} />
        <path d="M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2V17h5v-1.1c0-.8.4-1.5 1-2A6 6 0 0 0 12 3z" {...stroke(p)} />
    </svg>
);

// ⚡ 번개(급번개/플래시)
export const IcBolt: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" {...stroke(p)} />
    </svg>
);

// 🔥 불꽃(인기)
export const IcFlame: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M12 3c3 4 6 6 6 10a6 6 0 0 1-12 0c0-2 1-3.5 2.5-5C9 9.5 10 8 9.5 6 11 6.5 12 4.5 12 3z" {...stroke(p)} />
    </svg>
);

// ✨ 🌟 반짝임
export const IcSparkle: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M12 3l1.6 5.4L19 10l-5.4 1.6L12 17l-1.6-5.4L5 10l5.4-1.6L12 3z" {...stroke(p)} />
        <path d="M18 4v3M19.5 5.5h-3" {...stroke(p)} />
    </svg>
);

// 🎯 과녁
export const IcTarget: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <circle cx="12" cy="12" r="8.5" {...stroke(p)} />
        <circle cx="12" cy="12" r="5" {...stroke(p)} />
        <circle cx="12" cy="12" r="1.6" {...stroke(p)} />
    </svg>
);

// 💬 말풍선(채팅)
export const IcChat: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7a2.5 2.5 0 0 1-2.5 2.5H10l-4 4v-4H6.5A2.5 2.5 0 0 1 4 13.5v-7z" {...stroke(p)} />
    </svg>
);

// 💌 초대장(편지+하트) ✉️ 📨 📩
export const IcEnvelope: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <rect x="3" y="5" width="18" height="14" rx="2.5" {...stroke(p)} />
        <path d="M4 7l8 6 8-6" {...stroke(p)} />
    </svg>
);

// 🔔 알림(벨)
export const IcBell: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M6 9a6 6 0 0 1 12 0c0 5 1.5 7 1.5 7H4.5S6 14 6 9z" {...stroke(p)} />
        <path d="M10 19a2 2 0 0 0 4 0" {...stroke(p)} />
    </svg>
);

// 🔍 검색
export const IcSearch: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <circle cx="11" cy="11" r="7" {...stroke(p)} />
        <path d="M21 21l-4.3-4.3" {...stroke(p)} />
    </svg>
);

// ✏️ 📝 편집(연필)
export const IcEdit: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M4 20h4L19 9l-4-4L4 16v4z" {...stroke(p)} />
        <path d="M13.5 6.5l4 4" {...stroke(p)} />
    </svg>
);

// 📋 클립보드
export const IcClipboard: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <rect x="5" y="5" width="14" height="16" rx="2" {...stroke(p)} />
        <rect x="9" y="3" width="6" height="4" rx="1.2" {...stroke(p)} />
        <path d="M9 12h6M9 16h4" {...stroke(p)} />
    </svg>
);

// 📊 차트(통계)
export const IcChart: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M4 20V4M4 20h16" {...stroke(p)} />
        <path d="M8 20v-6M12 20v-9M16 20v-4" {...stroke(p)} />
    </svg>
);

// 📤 공유/내보내기 🔗 외부
export const IcShare: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M12 15V4M8.5 7.5L12 4l3.5 3.5" {...stroke(p)} />
        <path d="M6 12v6a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-6" {...stroke(p)} />
    </svg>
);

// 🔗 링크
export const IcLink: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M10 14a4 4 0 0 0 5.6 0l2.4-2.4a4 4 0 0 0-5.6-5.6L11 7.4" {...stroke(p)} />
        <path d="M14 10a4 4 0 0 0-5.6 0L6 12.4a4 4 0 0 0 5.6 5.6L13 16.6" {...stroke(p)} />
    </svg>
);

// 🔄 🔁 새로고침/반복
export const IcRefresh: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M20 11a8 8 0 0 0-14-4L4 9M4 4v5h5" {...stroke(p)} />
        <path d="M4 13a8 8 0 0 0 14 4l2-2M20 20v-5h-5" {...stroke(p)} />
    </svg>
);

// 🔒 잠금
export const IcLock: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <rect x="5" y="10" width="14" height="10" rx="2.2" {...stroke(p)} />
        <path d="M8 10V8a4 4 0 0 1 8 0v2" {...stroke(p)} />
    </svg>
);

// 🚫 금지
export const IcBan: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <circle cx="12" cy="12" r="8.5" {...stroke(p)} />
        <path d="M6 6l12 12" {...stroke(p)} />
    </svg>
);

// ⚠️ 경고
export const IcWarning: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M12 3.5l9.5 16.5H2.5L12 3.5z" {...stroke(p)} />
        <path d="M12 9.5v4.5M12 17h.01" {...stroke(p)} />
    </svg>
);

// ❓ 물음표
export const IcQuestion: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <circle cx="12" cy="12" r="8.5" {...stroke(p)} />
        <path d="M9.5 9.5a2.5 2.5 0 0 1 4.6 1.3c0 1.7-2.1 2-2.1 3.2M12 17h.01" {...stroke(p)} />
    </svg>
);

// 🎁 선물
export const IcGift: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <rect x="4" y="9" width="16" height="11" rx="1.5" {...stroke(p)} />
        <path d="M4 13h16M12 9v11" {...stroke(p)} />
        <path d="M12 9C12 6 9 5 8 6.5S9 9 12 9zM12 9c0-3 3-4 4-2.5S15 9 12 9z" {...stroke(p)} />
    </svg>
);

// 🎫 🎟 티켓
export const IcTicket: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M4 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2a2 2 0 0 0 0 6v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-6V7z" {...stroke(p)} />
        <path d="M13 5v14" strokeDasharray="2 2" {...stroke(p)} />
    </svg>
);

// 🗺️ 지도
export const IcMap: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M9 4L4 6v14l5-2 6 2 5-2V4l-5 2-6-2z" {...stroke(p)} />
        <path d="M9 4v14M15 6v14" {...stroke(p)} />
    </svg>
);

// 🏛️ 🏯 🏰 관광/랜드마크(기둥)
export const IcLandmark: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M12 3l8 4H4l8-4z" {...stroke(p)} />
        <path d="M6 10v7M10 10v7M14 10v7M18 10v7M4 20h16" {...stroke(p)} />
    </svg>
);

// 📷 📸 카메라
export const IcCamera: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M3 8.5A2 2 0 0 1 5 6.5h2l1.2-2h7.6L17 6.5h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-9z" {...stroke(p)} />
        <circle cx="12" cy="13" r="3.2" {...stroke(p)} />
    </svg>
);

// 📱 폰
export const IcPhone: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <rect x="6" y="3" width="12" height="18" rx="2.5" {...stroke(p)} />
        <path d="M11 18h2" {...stroke(p)} />
    </svg>
);

// 🎤 마이크
export const IcMic: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <rect x="9" y="3" width="6" height="11" rx="3" {...stroke(p)} />
        <path d="M6 11a6 6 0 0 0 12 0M12 17v4M9 21h6" {...stroke(p)} />
    </svg>
);

// 👁️ 눈
export const IcEye: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" {...stroke(p)} />
        <circle cx="12" cy="12" r="3" {...stroke(p)} />
    </svg>
);

// 🤖 로봇(AI)
export const IcRobot: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <rect x="5" y="8" width="14" height="11" rx="2.5" {...stroke(p)} />
        <path d="M12 4v4M9 13h.01M15 13h.01M9 16h6M3 12v3M21 12v3" {...stroke(p)} />
    </svg>
);

// 🎮 게임 패드
export const IcGame: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M7 8h10a4 4 0 0 1 4 4l-1 5a2.5 2.5 0 0 1-4.4 1L14 16h-4l-1.6 2A2.5 2.5 0 0 1 4 17l-1-5a4 4 0 0 1 4-4z" {...stroke(p)} />
        <path d="M7.5 12v2M6.5 13h2M15.5 12h.01M17 13.5h.01" {...stroke(p)} />
    </svg>
);

// 🎨 팔레트
export const IcPalette: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M12 3a9 9 0 0 0 0 18c1.5 0 2-1 2-2s-.8-1.6-.8-2.6 1-1.4 2-1.4H17a4 4 0 0 0 4-4c0-4.4-4-8-9-8z" {...stroke(p)} />
        <path d="M7.5 12h.01M9 8.5h.01M12.5 7h.01M15.5 8.5h.01" {...stroke(p)} />
    </svg>
);

// 🛍️ 👜 쇼핑백
export const IcShopping: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M5 8h14l-1 12H6L5 8z" {...stroke(p)} />
        <path d="M9 9V7a3 3 0 0 1 6 0v2" {...stroke(p)} />
    </svg>
);

// 🚧 공사중
export const IcConstruction: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <rect x="3" y="9" width="18" height="7" rx="1.5" {...stroke(p)} />
        <path d="M5 9l4 7M11 9l4 7M5 16V20M19 16V20M5 6V9M19 6V9" {...stroke(p)} />
    </svg>
);

// 🏃 달리기
export const IcRun: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <circle cx="15" cy="5" r="2" {...stroke(p)} />
        <path d="M14 21l-1.5-5-3-2 2-5 3 2 3 1M8 12l-3 1M11 16l-3 5" {...stroke(p)} />
    </svg>
);

// 🏋️ 운동(역기)
export const IcGym: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M5 9v6M3 11v2M19 9v6M21 11v2M7 12h10M9 8v8M15 8v8" {...stroke(p)} />
    </svg>
);

// 🎯 위 IcTarget. 🎰 슬롯머신
export const IcSlot: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <rect x="3" y="7" width="13" height="13" rx="2" {...stroke(p)} />
        <path d="M6.5 11h6M6.5 14h6M16 9h2a2 2 0 0 1 2 2v3M20 14l-1.5 3" {...stroke(p)} />
    </svg>
);

// 🪜 사다리
export const IcLadder: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M8 3v18M16 3v18M8 7h8M8 11h8M8 15h8" {...stroke(p)} />
    </svg>
);

// 🎲 주사위 🎳 볼링은 근사
export const IcDice: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <rect x="4" y="4" width="16" height="16" rx="3" {...stroke(p)} />
        <path d="M9 9h.01M15 9h.01M12 12h.01M9 15h.01M15 15h.01" {...stroke(p)} />
    </svg>
);

// 🚕 택시(이동)
export const IcTaxi: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M4 16v-3l2-5h12l2 5v3" {...stroke(p)} />
        <path d="M3 16h18v3h-3v-3M6 19v-3M9 5h6" {...stroke(p)} />
        <circle cx="7.5" cy="16.5" r="1.3" {...stroke(p)} />
        <circle cx="16.5" cy="16.5" r="1.3" {...stroke(p)} />
    </svg>
);

// 🌙 달
export const IcMoon: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M20 13.5A8 8 0 0 1 10.5 4 7 7 0 1 0 20 13.5z" {...stroke(p)} />
    </svg>
);

// ☀️ 해
export const IcSun: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <circle cx="12" cy="12" r="4" {...stroke(p)} />
        <path d="M12 2v2M12 20v2M4 12H2M22 12h-2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19" {...stroke(p)} />
    </svg>
);

// 📞 전화
export const IcCall: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M5 4h3l1.5 4-2 1.5a11 11 0 0 0 5 5l1.5-2 4 1.5V18a2 2 0 0 1-2 2A15 15 0 0 1 5 6a2 2 0 0 1 0-2z" {...stroke(p)} />
    </svg>
);

// 📢 확성기(공지)
export const IcMegaphone: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M4 10v4a1 1 0 0 0 1 1h2l9 4V5L7 9H5a1 1 0 0 0-1 1z" {...stroke(p)} />
        <path d="M8 15v3M19 9a3 3 0 0 1 0 6" {...stroke(p)} />
    </svg>
);

// 🧭 나침반
export const IcCompass: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <circle cx="12" cy="12" r="9" {...stroke(p)} />
        <path d="M16 8l-2.5 5.5L8 16l2.5-5.5L16 8z" {...stroke(p)} />
    </svg>
);

// 🗑 휴지통
export const IcTrash: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" {...stroke(p)} />
    </svg>
);

// ➕ 추가
export const IcPlus: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M12 5v14M5 12h14" {...stroke(p)} />
    </svg>
);

// 👍 좋아요
export const IcThumbsUp: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M7 10v10H4V10h3z" {...stroke(p)} />
        <path d="M7 10l4-7a2 2 0 0 1 2 2v3h5a2 2 0 0 1 2 2.3l-1.2 6A2 2 0 0 1 18 20H7" {...stroke(p)} />
    </svg>
);

// 😊 😂 😔 😢 🤔 표정(중립 미소) — 콘텐츠 이모지 대체용 일반 표정
export const IcSmile: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <circle cx="12" cy="12" r="9" {...stroke(p)} />
        <path d="M8.5 14a4 4 0 0 0 7 0M9 9.5h.01M15 9.5h.01" {...stroke(p)} />
    </svg>
);

// ℹ️ 정보
export const IcInfo: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <circle cx="12" cy="12" r="9" {...stroke(p)} />
        <path d="M12 11v5M12 8h.01" {...stroke(p)} />
    </svg>
);

// 🗳️ 투표함
export const IcBallot: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <rect x="3" y="11" width="18" height="9" rx="2" {...stroke(p)} />
        <path d="M8 11V8a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v3M9 14h6" {...stroke(p)} />
    </svg>
);

// 🎳 볼링
export const IcBowling: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <circle cx="10" cy="13" r="7" {...stroke(p)} />
        <path d="M8 10h.01M11 9h.01M11 12h.01M18 4c1.5 2 1.5 6 0 8M21 3c2 3 2 8 0 11" {...stroke(p)} />
    </svg>
);

// ⛳ 골프
export const IcGolf: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M12 3v15" {...stroke(p)} />
        <path d="M12 3l6 2.5L12 8" {...stroke(p)} />
        <path d="M7 20c1.5-1 8.5-1 10 0" {...stroke(p)} />
    </svg>
);

// 🙈 비밀번호 숨김(눈 가림)
export const IcEyeOff: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M2.5 12S6 5.5 12 5.5c1.5 0 2.9.4 4.1 1M21.5 12s-1.2 2.2-3.4 4M9.9 9.9a3 3 0 0 0 4.2 4.2" {...stroke(p)} />
        <path d="M3 3l18 18" {...stroke(p)} />
    </svg>
);

// 🐢 거북이(꼴찌/느림)
export const IcTurtle: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M5 16a7 7 0 0 1 14 0" {...stroke(p)} />
        <path d="M3 16h2.5M18.5 16H21M7 16l-1.5 3M17 16l1.5 3M12 9V6" {...stroke(p)} />
        <path d="M19 13c1 0 2 .5 2 1.5S20 16 19 16" {...stroke(p)} />
    </svg>
);

// 🌐 🌍 🌏 지구/언어
export const IcGlobe: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <circle cx="12" cy="12" r="9" {...stroke(p)} />
        <path d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18" {...stroke(p)} />
    </svg>
);

// 📜 📋 문서/약관
export const IcDocument: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M6 3h8l4 4v14a0 0 0 0 1 0 0H6a0 0 0 0 1 0 0V3z" {...stroke(p)} />
        <path d="M14 3v4h4M8 12h8M8 16h6" {...stroke(p)} />
    </svg>
);

// 🐛 벌레(디버그)
export const IcBug: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <rect x="8" y="7" width="8" height="11" rx="4" {...stroke(p)} />
        <path d="M9 7a3 3 0 0 1 6 0M4 11h4M16 11h4M4 16h4M16 16h4M12 7V4M10 4h4" {...stroke(p)} />
    </svg>
);

// 🚄 🚉 🚂 기차/역
export const IcTrain: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <rect x="6" y="4" width="12" height="13" rx="3" {...stroke(p)} />
        <path d="M6 10h12M9 17l-2 3M15 17l2 3" {...stroke(p)} />
        <circle cx="9.5" cy="13.5" r="1" {...stroke(p)} />
        <circle cx="14.5" cy="13.5" r="1" {...stroke(p)} />
    </svg>
);

// 💆 ♨️ 휴식/온천(스파)
export const IcSpa: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M5 21h14a6 6 0 0 0-14 0z" {...stroke(p)} />
        <path d="M9 9c0-2 1.5-3 1.5-3M9 9s2-1 2-3M14.5 9c0-2-1.5-3-1.5-3M14.5 9s-2-1-2-3" {...stroke(p)} />
    </svg>
);

// ◀ ← 이전(chevron)
export const IcChevronLeft: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M15 6l-6 6 6 6" {...stroke(p)} />
    </svg>
);

// ▶ → 다음(chevron)
export const IcChevronRight: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <path d="M9 6l6 6-6 6" {...stroke(p)} />
    </svg>
);

// ⋯ 3점(더보기)
export const IcMore: React.FC<IconProps> = (p) => (
    <svg {...S(p.size)} {...base(p)}>
        <circle cx="5" cy="12" r="1.5" fill={p.color || 'currentColor'} stroke="none" />
        <circle cx="12" cy="12" r="1.5" fill={p.color || 'currentColor'} stroke="none" />
        <circle cx="19" cy="12" r="1.5" fill={p.color || 'currentColor'} stroke="none" />
    </svg>
);

/**
 * 이모지 → 컴포넌트 매핑(동일 이모지=동일 SVG 강제용 단일 소스).
 * 치환 시 이 표를 기준으로 사용.
 */
export const EMOJI_ICON: Record<string, React.FC<IconProps>> = {
    '📍': IcPin, '📌': IcPushpin,
    '📅': IcCalendar, '📆': IcCalendar, '🗓️': IcCalendar, '🗓': IcCalendar,
    '✕': IcClose, '❌': IcClose, '✖️': IcClose,
    '✓': IcCheck, '✔️': IcCheck,
    '✅': IcCheckCircle, '☑': IcCheckCircle, '☑️': IcCheckCircle,
    '☐': IcSquare,
    '🏁': IcFinishFlag, '🚩': IcFlag,
    '🏨': IcHotel, '🛏️': IcHotel, '🛏': IcHotel,
    '⏰': IcClock, '🕐': IcClock, '🕰': IcClock, '🕰️': IcClock, '⏱️': IcClock,
    '⏳': IcHourglass, '⌛': IcHourglass,
    '✈️': IcPlane, '✈': IcPlane, '🛫': IcPlane, '🛬': IcPlane,
    '🏠': IcHome, '🏡': IcHome,
    '🍽️': IcRestaurant, '🍽': IcRestaurant, '🍴': IcRestaurant,
    '☕': IcCafe, '🧋': IcCafe, '🍵': IcCafe,
    '🍺': IcBeer, '🍻': IcBeer,
    '🍾': IcChampagne, '🥂': IcChampagne, '🍷': IcChampagne,
    '👥': IcUsers, '👫': IcUsers,
    '👤': IcUser,
    '🙋': IcRaiseHand, '🙆': IcRaiseHand, '🙅': IcRaiseHand, '✋': IcRaiseHand,
    '👋': IcWave,
    '👑': IcCrown,
    '🏆': IcTrophy,
    '🥇': IcMedal, '🥈': IcMedal, '🥉': IcMedal,
    '🎉': IcParty, '🎊': IcParty,
    '★': IcStar, '⭐': IcStar, '🌟': IcStar,
    '☆': IcStarOutline,
    '❤': IcHeart, '❤️': IcHeart, '🤍': IcHeart, '♡': IcStarOutline,
    '💰': IcMoney,
    '💸': IcExpense,
    '💳': IcCard,
    '💡': IcBulb,
    '⚡': IcBolt,
    '🔥': IcFlame,
    '✨': IcSparkle,
    '🎯': IcTarget,
    '💬': IcChat,
    '💌': IcEnvelope, '✉️': IcEnvelope, '✉': IcEnvelope, '📨': IcEnvelope, '📩': IcEnvelope, '📥': IcEnvelope, '📭': IcEnvelope,
    '🔔': IcBell,
    '🔍': IcSearch,
    '✏️': IcEdit, '✏': IcEdit, '📝': IcEdit,
    '📋': IcClipboard,
    '📊': IcChart,
    '📤': IcShare,
    '🔗': IcLink,
    '🔄': IcRefresh, '🔁': IcRefresh,
    '🔒': IcLock,
    '🚫': IcBan,
    '⚠️': IcWarning, '⚠': IcWarning,
    '❓': IcQuestion, '❔': IcQuestion,
    '🎁': IcGift,
    '🎫': IcTicket, '🎟️': IcTicket, '🎟': IcTicket,
    '🗺️': IcMap, '🗺': IcMap,
    '🏛️': IcLandmark, '🏛': IcLandmark, '🏯': IcLandmark, '🏰': IcLandmark,
    '📷': IcCamera, '📸': IcCamera,
    '📱': IcPhone,
    '🎤': IcMic,
    '👁️': IcEye, '👁': IcEye,
    '🤖': IcRobot,
    '🎮': IcGame,
    '🎨': IcPalette,
    '🛍️': IcShopping, '🛍': IcShopping, '👜': IcShopping,
    '🚧': IcConstruction,
    '🏃': IcRun,
    '🏋️': IcGym, '🏋': IcGym,
    '🎰': IcSlot,
    '🪜': IcLadder,
    '🎲': IcDice,
    '🚕': IcTaxi,
    '🌙': IcMoon,
    '☀️': IcSun, '☀': IcSun,
    '📞': IcCall,
    '📢': IcMegaphone,
    '🧭': IcCompass,
    '🗑': IcTrash, '🗑️': IcTrash,
    '➕': IcPlus,
    '👍': IcThumbsUp,
    '😊': IcSmile, '😂': IcSmile, '😔': IcSmile, '😢': IcSmile, '🤔': IcSmile,
    'ℹ️': IcInfo, 'ℹ': IcInfo,
    '🗳️': IcBallot, '🗳': IcBallot,
    '🎳': IcBowling,
    '⛳': IcGolf, '⛳️': IcGolf,
    '🙈': IcEyeOff, '🙉': IcEyeOff, '🙊': IcEyeOff,
    '🐢': IcTurtle,
    '🌐': IcGlobe, '🌍': IcGlobe, '🌏': IcGlobe, '🌎': IcGlobe,
    '📜': IcDocument,
    '🐛': IcBug,
    '🚄': IcTrain, '🚉': IcTrain, '🚂': IcTrain, '🚆': IcTrain,
    '💆': IcSpa, '♨️': IcSpa, '♨': IcSpa,
};
